import { getPublicKey, ZionApiError } from '../api/ZionApiClient.ts'
import { normalizeNickname } from '../crypto/ZionCipher.ts'
import {
  getContactPublicKeyBackup,
  saveContactPublicKeyBackup,
} from '../storage/keyStore.ts'
import {
  findContact,
  listContacts,
  type ZionContact,
} from '../storage/pairingStore.ts'
import { getSessionToken } from '../storage/sessionStore.ts'

export const CONTACT_KEY_MISSING_ERROR = 'Chiave pubblica del contatto non trovata sul server.'

export function isContactPublicKeyMissing(key: string | null | undefined): boolean {
  return !String(key ?? '').trim()
}

/** Varianti nickname per lookup locale (cleverviper#5976 → cleverviper). */
function nicknameLookupKeys(nickname: string): string[] {
  const raw = normalizeNickname(nickname)
  if (!raw) return []
  const keys = [raw]
  const base = raw.split('#')[0]?.trim()
  if (base && base !== raw) keys.push(base)
  return keys
}

/**
 * Chiave locale: record contatto → IndexedDB (nickname pieno / base / sessionToken).
 * ZERO rete.
 */
export async function resolveLocalPublicKey(contact: ZionContact): Promise<string | null> {
  const fromContact = contact.publicKey?.trim()
  if (fromContact) return fromContact

  for (const nick of nicknameLookupKeys(contact.nickname)) {
    const byNick = await getContactPublicKeyBackup(nick, '')
    if (byNick?.trim()) return byNick.trim()
  }

  const bySess = await getContactPublicKeyBackup('', contact.sessionToken)
  return bySess?.trim() || null
}

/**
 * Fetch server SOLO quando non c'è chiave locale.
 * Un solo tentativo: 404/410 → null subito (niente loop URL).
 */
export async function fetchContactPublicKey(
  nickname: string,
  token?: string | null,
  _maxAttempts = 1,
): Promise<string | null> {
  const normalized = normalizeNickname(nickname)
  if (!normalized) return null

  const authToken = token?.trim() || (await getSessionToken())?.trim() || undefined
  try {
    const fresh = await getPublicKey(normalized, authToken)
    if (fresh?.trim()) {
      await saveContactPublicKeyBackup(normalized, '', fresh.trim())
      const base = normalized.split('#')[0]?.trim()
      if (base && base !== normalized) {
        await saveContactPublicKeyBackup(base, '', fresh.trim())
      }
      return fresh.trim()
    }
    return null
  } catch (e) {
    const status = e instanceof ZionApiError ? e.status : 0
    if (status === 404 || status === 410) return null
    return null
  }
}

/** Aggiorna il contatto in IndexedDB con una chiave valida (atomico per contactId). */
export async function updateContactPublicKey(
  contact: ZionContact,
  publicKey: string,
): Promise<ZionContact> {
  const trimmed = publicKey.trim()
  const { saveContactAtomic } = await import('../storage/pairingStore.ts')
  const updated = await saveContactAtomic(contact.contactId, (current) => {
    const base = current ?? contact
    return { ...base, publicKey: trimmed }
  })
  await saveContactPublicKeyBackup(contact.nickname, contact.sessionToken, trimmed)
  const baseNick = normalizeNickname(contact.nickname).split('#')[0]?.trim()
  if (baseNick) await saveContactPublicKeyBackup(baseNick, '', trimmed)
  return updated ?? { ...contact, publicKey: trimmed }
}

/**
 * LOCAL FIRST — se la chiave è in contatto/IndexedDB: ritorna subito, ZERO HTTP.
 * forceRefresh è ignorato se esiste chiave locale (Zero-Knowledge: server spesso 410).
 * Solo senza chiave locale si tenta UNA chiamata server.
 */
export async function ensureContactWithPublicKey(
  contact: ZionContact,
  _forceRefresh = false,
): Promise<ZionContact> {
  const local = await resolveLocalPublicKey(contact)

  if (local) {
    if (contact.publicKey.trim() === local) {
      await saveContactPublicKeyBackup(contact.nickname, contact.sessionToken, local)
      return contact
    }
    return updateContactPublicKey(contact, local)
  }

  // Nessuna chiave locale → un solo tentativo server (no loop)
  const fromServer = await fetchContactPublicKey(contact.nickname, undefined, 1)
  if (fromServer) {
    return updateContactPublicKey(contact, fromServer)
  }

  throw new Error(
    `${CONTACT_KEY_MISSING_ERROR} Contatto @${contact.nickname || 'sconosciuto'}. ` +
      'Riaccoppia il contatto se necessario.',
  )
}

export async function ensureContactPublicKeyById(contactId: string): Promise<ZionContact> {
  const contact = await findContact(contactId)
  if (!contact) {
    throw new Error('Contatto non trovato.')
  }
  return ensureContactWithPublicKey(contact)
}

/** All'avvio: ripristina chiavi mancanti da backup locale. Nessun fetch remoto. */
export async function hydrateAllContactsPublicKeys(): Promise<ZionContact[]> {
  const contacts = await listContacts()
  const repaired: ZionContact[] = []

  for (const contact of contacts) {
    try {
      if (!isContactPublicKeyMissing(contact.publicKey)) {
        await saveContactPublicKeyBackup(contact.nickname, contact.sessionToken, contact.publicKey)
        repaired.push(contact)
        continue
      }

      const fromBackup = await resolveLocalPublicKey(contact)
      if (fromBackup) {
        repaired.push(await updateContactPublicKey(contact, fromBackup))
        continue
      }

      repaired.push(contact)
    } catch {
      repaired.push(contact)
    }
  }

  return repaired
}
